import React, { useState } from 'react';
import { ArrowLeft, Calendar, User, FileText, Stethoscope, Edit2, Send, Loader2, ShieldCheck } from 'lucide-react';
import { UserRequest } from '../types';
import { GeneratedNotesComponent } from './GeneratedNotes';
import { PatientInput } from './PatientInput';
import { PatientModal } from './PatientModal';
import { api } from '../services/api';

interface RequestDetailProps {
  request: UserRequest;
  onBack: () => void;
  onPatientClick?: (patientId: string, request: UserRequest) => void;
  onRequestUpdate?: (updatedRequest: UserRequest) => void;
  onPushToEhr?: (request: UserRequest) => void | Promise<void>;
  pushStatus?: 'idle' | 'pushing' | 'success' | 'error';
  pushMessage?: string;
}

export const RequestDetail: React.FC<RequestDetailProps> = ({
  request,
  onBack,
  onPatientClick,
  onRequestUpdate,
  onPushToEhr,
  pushStatus = 'idle',
  pushMessage,
}) => {
  const [loading, setLoading] = useState(false);
  const [isEditingPatient, setIsEditingPatient] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(request.patientId || '');
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [refreshPatientsTrigger, setRefreshPatientsTrigger] = useState(0);
  const [isSavingPatient, setIsSavingPatient] = useState(false);
  const [localRequest, setLocalRequest] = useState(request);

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get task type icon
  const getTaskTypeIcon = (taskName: string) => {
    const taskNameLower = (taskName || '').toLowerCase();
    if (taskNameLower.includes('note') || taskNameLower.includes('soap')) {
      return <Stethoscope size={16} className="text-primary-content" />;
    }
    return <FileText size={16} className="text-primary" />;
  };

  // Handle document view
  const handleViewDocument = async (doc: any) => {
    try {
      setLoading(true);
      // Fetch the document from S3
      const pdfBlob = await api.fetchPdfBlob(doc.name);
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error fetching document:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if this is a notes task (any streaming note template)
  const isNotesTask = (localRequest.taskName || '').toLowerCase().includes('note') || 
                     (localRequest.taskName || '').toLowerCase().includes('soap') ||
                     (localRequest.taskName || '').toLowerCase().includes('history') ||
                     (localRequest.taskName || '').toLowerCase().includes('physical') ||
                     (localRequest.taskName || '').toLowerCase().includes('girpp') ||
                     (localRequest.taskName || '').toLowerCase().includes('birp') ||
                     (localRequest.taskName || '').toLowerCase().includes('sirp') ||
                     (localRequest.taskName || '').toLowerCase().includes('dap') ||
                     (localRequest.taskName || '').toLowerCase().includes('behavioral') ||
                     (localRequest.generatedNotes || []).length > 0;

  const handleSavePatient = async () => {
    if (!selectedPatient || selectedPatient === localRequest.patientId) {
      setIsEditingPatient(false);
      return;
    }

    try {
      setIsSavingPatient(true);
      const updatedRequest = await api.updateRequestPatient(localRequest.id, selectedPatient);
      setLocalRequest(updatedRequest);
      setIsEditingPatient(false);
      // Notify parent component of the update
      if (onRequestUpdate) {
        onRequestUpdate(updatedRequest);
      }
    } catch (error) {
      console.error('Error updating patient:', error);
      // Reset to original patient
      setSelectedPatient(localRequest.patientId || '');
    } finally {
      setIsSavingPatient(false);
    }
  };

  const handleCancelEditPatient = () => {
    setSelectedPatient(localRequest.patientId || '');
    setIsEditingPatient(false);
  };

  const handlePatientAdded = (patient: any) => {
    setRefreshPatientsTrigger(prev => prev + 1);
    setSelectedPatient(patient.id);
  };

  return (
    <div className="h-full flex flex-col bg-base-100">
      <div className="p-4 border-b border-base-300 bg-base-100 shadow-sm">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-base-content hover:text-primary transition-colors duration-200"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
      </div>

      {onPushToEhr && (
        <div className="p-4 border-b border-base-300 bg-base-100 flex flex-col gap-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-base font-semibold text-base-content">Push to EHR</h3>
              <p className="text-sm text-base-content/70">Automatically map this visit into your EHR</p>
            </div>
            <button
              onClick={() => onPushToEhr(localRequest)}
              className="btn btn-primary"
              disabled={pushStatus === 'pushing'}
            >
              {pushStatus === 'pushing' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Preparing...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Push to EHR</span>
                </>
              )}
            </button>
          </div>
          {pushMessage && (
            <div
              className={`flex items-center gap-2 text-sm ${
                pushStatus === 'error'
                  ? 'text-error'
                  : pushStatus === 'success'
                    ? 'text-success'
                    : 'text-base-content/70'
              }`}
            >
              {pushStatus === 'success' ? <ShieldCheck className="w-4 h-4" /> : null}
              <span>{pushMessage}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex-1 p-6 overflow-visible">
        <div className="space-y-6">
          {/* Request Info Section */}
          <div className="bg-base-100 rounded-lg p-6 space-y-4 shadow-sm border border-base-300">
            <h2 className="text-xl font-semibold text-base-content mb-4">Visit Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div className="flex items-start gap-3 p-4 bg-base-200 rounded-lg border border-base-300 relative overflow-visible">
                <User size={16} className="text-primary-content mt-0.5 flex-shrink-0" />
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-sm font-medium text-base-content/70">Patient</span>
                  {isEditingPatient ? (
                    <>
                      <div className="mt-1">
                        <PatientInput
                          selectedPatient={selectedPatient}
                          onPatientChange={setSelectedPatient}
                          onShowAddModal={() => setShowPatientModal(true)}
                          refreshTrigger={refreshPatientsTrigger}
                        />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={handleSavePatient}
                          disabled={isSavingPatient || !selectedPatient}
                          className="btn btn-primary btn-xs"
                        >
                          {isSavingPatient ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelEditPatient}
                          disabled={isSavingPatient}
                          className="btn btn-ghost btn-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mt-1">
                        {(() => {
                          const patientName = localRequest.patientName || 'No Patient Selected';
                          const hasPatient = localRequest.patientId && 
                                             localRequest.patientId.trim() !== '' && 
                                             patientName !== 'No Patient Selected' &&
                                             localRequest.patientName && 
                                             localRequest.patientName.trim() !== '';
                          
                          return onPatientClick && hasPatient ? (
                            <button
                              type="button"
                              onClick={() => onPatientClick(localRequest.patientId, localRequest)}
                              className="text-base-content font-semibold hover:text-base-content/70 text-left cursor-pointer bg-transparent border-none p-0 transition-colors"
                              style={{ cursor: 'pointer' }}
                            >
                              {patientName}
                            </button>
                          ) : (
                            <span className="text-base-content font-semibold">{patientName}</span>
                          );
                        })()}
                      </div>
                      <button
                        onClick={() => setIsEditingPatient(true)}
                        className="absolute top-2 right-2 p-1 hover:bg-base-300 rounded transition-colors text-primary hover:text-primary-focus cursor-pointer"
                        title="Edit patient"
                      >
                        <Edit2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-base-200 rounded-lg border border-base-300">
                <Calendar size={16} className="text-primary-content mt-0.5 flex-shrink-0" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-base-content/70">Date</span>
                  <span className="text-base-content font-semibold">{formatDate(localRequest.createdAt)}</span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-base-200 rounded-lg border border-base-300">
                <div className="mt-0.5 flex-shrink-0">
                  {getTaskTypeIcon(localRequest.taskName)}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-base-content/70">Visit Type</span>
                  <span className="text-base-content font-semibold">{localRequest.taskName || 'Unknown Task'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-base-100 rounded-lg p-6 shadow-sm border border-base-300">
            {isNotesTask ? (
              <GeneratedNotesComponent
                finalResult={{
                  generatedNotes: localRequest.generatedNotes,
                  generatedDocuments: [],
                  taskDefinitionId: localRequest.id, // Use request ID as fallback
                  output: '',
                  success: localRequest.status === 'Completed',
                  error: '', // No error field in new DTO
                  metadata: {},
                  stepResults: []
                }}
                userRequest={localRequest}
              />
            ) : (
              <div className="space-y-4">
                <h4 className="text-md font-medium text-base-content">Generated Documents</h4>
                <div className="space-y-3">
                  {(localRequest.generatedDocumentS3Keys || []).map((docInfo, index) => {
                    const fileName = docInfo.documentName || (docInfo.s3Key || '').split('/').pop() || docInfo.s3Key || 'Unknown Document';

                    return (
                      <div key={index} className="flex items-center justify-between p-4 bg-base-200 rounded-lg border border-base-300">
                        <div className="flex items-center gap-3">
                          <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                          <span className="font-medium text-base-content">{fileName}</span>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            className="btn btn-outline btn-sm  border-[0.5px]"
                            onClick={() => handleViewDocument({ name: docInfo.s3Key || 'Unknown Document' })}
                            title="Preview document"
                            disabled={loading}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            {loading ? 'Loading...' : 'Preview'}
                          </button>
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={async () => {
                              try {
                                setLoading(true);
                                const pdfBlob = await api.fetchPdfBlob(docInfo.s3Key || '');
                                const url = URL.createObjectURL(pdfBlob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = fileName;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              } catch (err) {
                                console.error('Error downloading document:', err);
                              } finally {
                                setLoading(false);
                              }
                            }}
                            title="Download document"
                            disabled={loading}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {loading ? 'Loading...' : 'Download'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Patient Modal */}
      <PatientModal
        isVisible={showPatientModal}
        onClose={() => setShowPatientModal(false)}
        onPatientAdded={handlePatientAdded}
      />
    </div>
  );
}; 